import React from 'react';
import clsx from 'clsx';
import type { BrowseResultType } from 'fym-shared/src/music24/browseSearch';
import { TypedMemo } from '../../../common/types';
import type { useBrowseSearch } from '../hooks/useBrowseSearch';
import type { BrowseUsageData } from '../hooks/useBrowseUsage';
import { BrowseSkeleton } from './BrowseSkeleton';
import { BrowseTopResult } from './BrowseTopResult';
import { BrowseArtistsSection, BrowsePlaylistsSection, BrowseTracksSection } from './BrowseTypeSections';
import { BrowseSearchError } from '../states/BrowseSearchError';
import { BrowseNoResults } from '../states/BrowseNoResults';
import { BrowseCreditsUsedUp } from '../rail/BrowseCreditsUsedUp';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseResultsProps {
  search: ReturnType<typeof useBrowseSearch>;
  query: string;
  type: BrowseResultType;
  filterOn: boolean;
  usage: BrowseUsageData | undefined;
  onTypeChange: (type: BrowseResultType) => void;
  onShowMore: () => void;
  onSearch: (query: string) => void;
  onClearFilters: () => void;
  onPasteLink: () => void;
}

/**
 * The result area (design a1, b2–b5). The previous answer stays while the next one loads and only
 * fades (b3); the skeleton shows only before the first answer. A failed search shows the error,
 * never "No results" (b4).
 */
const BrowseResultsComponent: React.FC<BrowseResultsProps> = ({
  search,
  query,
  type,
  filterOn,
  usage,
  onTypeChange,
  onShowMore,
  onSearch,
  onClearFilters,
  onPasteLink,
}) => {
  const { data } = search;
  if (search.isError) {
    return (
      <BrowseSearchError
        error={search.error}
        onRetry={() => {
          search.refetch().catch(console.error);
        }}
      />
    );
  }
  if (data === undefined) {
    return <BrowseSkeleton />;
  }
  // One type tab counts only its own rows: "Playlists 0" is no result even when artists match.
  const empty = type === 'all' ? data.counts.all === 0 : data.counts[type] === 0;
  if (empty && !search.isPlaceholderData) {
    return (
      <BrowseNoResults
        query={query}
        didYouMean={data.didYouMean}
        filterOn={filterOn}
        onSearch={onSearch}
        onClearFilters={onClearFilters}
        onPasteLink={onPasteLink}
      />
    );
  }
  const onAll = type === 'all';
  return (
    <div
      className={clsx(styles.sectionStack, search.isFetching && search.isPlaceholderData && styles.loadingVeil)}
      aria-busy={search.isFetching}
    >
      {usage !== undefined && usage.left <= 0 && <BrowseCreditsUsedUp usage={usage} />}
      {onAll && data.top !== null && <BrowseTopResult top={data.top} numbersDate={data.numbersDate} />}
      {data.artists !== null && data.artists.items.length > 0 && (
        <BrowseArtistsSection
          page={data.artists}
          onSeeAll={onAll ? () => onTypeChange('artists') : undefined}
          onShowMore={onAll ? undefined : onShowMore}
        />
      )}
      {data.tracks !== null && data.tracks.items.length > 0 && (
        <BrowseTracksSection
          page={data.tracks}
          onSeeAll={onAll ? () => onTypeChange('tracks') : undefined}
          onShowMore={onAll ? undefined : onShowMore}
        />
      )}
      {data.playlists !== null && data.playlists.items.length > 0 && (
        <BrowsePlaylistsSection
          page={data.playlists}
          onSeeAll={onAll ? () => onTypeChange('playlists') : undefined}
          onShowMore={onAll ? undefined : onShowMore}
        />
      )}
    </div>
  );
};

export const BrowseResults = TypedMemo(BrowseResultsComponent);
